import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import Menu, { MenuProps, NavItemTypeEnum } from '@lib/components/Menu/Menu';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

export default function ExtraFooter({
  onChangeHandler,
  selected,
  minDate,
  maxDate,
}: {
  onChangeHandler: (v: [Dayjs | null, Dayjs | null] | null) => void;
  selected: (Dayjs | null)[] | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}) {
  const { t } = useTranslation();
  const today = dayjs().endOf('day');
  const dateRanges: MenuProps<object>['navItems'] = [
    {
      id: 'today',
      title: t('today'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => onChangeHandler([today.startOf('day'), today]),
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.startOf('day'), 'day') &&
          selected[1].isSame(today, 'day')
        );
      },
    },
    {
      id: 'yesterday',
      title: t('yesterday'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () =>
        onChangeHandler([
          today.subtract(1, 'day').startOf('day'),
          today.subtract(1, 'day').endOf('day'),
        ]),
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.subtract(1, 'day').startOf('day'), 'day') &&
          selected[1].isSame(today.subtract(1, 'day').endOf('day'), 'day')
        );
      },
    },
    {
      id: 'last7days',
      title: t('last-7-days'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () =>
        onChangeHandler([today.subtract(6, 'day').startOf('day'), today]),
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.subtract(6, 'day').startOf('day'), 'day') &&
          selected[1].isSame(today, 'day')
        );
      },
    },
    {
      id: 'last30days',
      title: t('last-30-days'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.subtract(29, 'day').startOf('day');
        onChangeHandler([start, today]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.subtract(29, 'day').startOf('day'), 'day') &&
          selected[1].isSame(today, 'day')
        );
      },
    },
    {
      id: 'thisweek',
      title: t('this-week'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        onChangeHandler([today.startOf('week'), today]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.startOf('week'), 'day') &&
          selected[1].isSame(today, 'day')
        );
      },
    },
    {
      id: 'lastWeek',
      title: t('last-week'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.subtract(1, 'week').startOf('week');
        const end = today.subtract(1, 'week').endOf('week');
        onChangeHandler([start, end]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        const start = today.subtract(1, 'week').startOf('week');
        const end = today.subtract(1, 'week').endOf('week');
        return (
          selected[0].isSame(start, 'day') && selected[1].isSame(end, 'day')
        );
      },
    },
    {
      id: 'thisMonth',
      title: t('this-month'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.startOf('month');
        onChangeHandler([start, today]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.startOf('month'), 'day') &&
          selected[1].isSame(today, 'day')
        );
      },
    },
    {
      id: 'lastMonth',
      title: t('last-month'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.subtract(1, 'month').startOf('month');
        const end = today.subtract(1, 'month').endOf('month');
        onChangeHandler([start, end]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        const start = today.subtract(1, 'month').startOf('month');
        const end = today.subtract(1, 'month').endOf('month');
        return (
          selected[0].isSame(start, 'day') && selected[1].isSame(end, 'day')
        );
      },
    },
    {
      id: 'thisQuarter',
      title: t('this-quarter'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.startOf('quarter');
        onChangeHandler([start, today]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        const startOfQuarter = today.startOf('quarter');
        const durationInMonths = selected[1].diff(selected[0], 'month', true);
        return (
          selected[0].isSame(startOfQuarter, 'day') &&
          selected[1].isSame(today, 'day') &&
          durationInMonths > 1
        );
      },
    },
    {
      id: 'lastQuarter',
      title: t('last-quarter'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const lastQuarter = today.subtract(3, 'month');
        const start = lastQuarter.startOf('quarter');
        const end = lastQuarter.endOf('quarter');
        onChangeHandler([start, end]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        const lastQuarter = today.subtract(3, 'month');
        const start = lastQuarter.startOf('quarter');
        const end = lastQuarter.endOf('quarter');
        return (
          selected[0].isSame(start, 'day') && selected[1].isSame(end, 'day')
        );
      },
    },
    {
      id: 'thisYear',
      title: t('this-year'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.startOf('year');
        onChangeHandler([start, today]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        return (
          selected[0].isSame(today.startOf('year'), 'day') &&
          selected[1].isSame(today, 'day')
        );
      },
    },
    {
      id: 'lastYear',
      title: t('last-year'),
      type: NavItemTypeEnum.PrimaryText,
      onClick: () => {
        const start = today.subtract(1, 'year').startOf('year');
        const end = today.subtract(1, 'year').endOf('year');
        onChangeHandler([start, end]);
      },
      predictorFnActive: () => {
        if (!selected || !selected[0] || !selected[1]) return false;
        const start = today.subtract(1, 'year').startOf('year');
        const end = today.subtract(1, 'year').endOf('year');
        return (
          selected[0].isSame(start, 'day') && selected[1].isSame(end, 'day')
        );
      },
    },
    {
      id: 'allTime',
      title: t('all-time'),
      type: NavItemTypeEnum.PrimaryText,
      // With a bounded picker, "All time" spans the whole allowed range
      // instead of clearing the value (which is invalid for required fields).
      onClick: () => {
        onChangeHandler(minDate && maxDate ? [minDate, maxDate] : [null, null]);
      },
      predictorFnActive: () => {
        if (minDate && maxDate) {
          return Boolean(
            selected?.[0]?.isSame(minDate, 'day') &&
            selected?.[1]?.isSame(maxDate, 'day'),
          );
        }
        return (
          selected === null || (selected[0] === null && selected[1] === null)
        );
      },
    },
  ];

  return <Menu navItems={dateRanges} />;
}
